/**
 * The shared Supply chrome dictionary — every label the tab bar, the More
 * sheet and the header toggle render, keyed by the SAME SupplyNavKey the nav
 * model (./nav.ts) already uses. One `Copy` pair per surface, routed through
 * `pick`/`navLabel` so there is exactly one place a caller resolves a
 * language — see ./lang.ts's docblock for the split rationale.
 *
 * Register (binding, see task-2 brief): BM is everyday Malaysian workplace
 * BM — hand-written, short, action-first ("Order", "Terima").
 * EN is the existing plain-English label already live in nav.ts / the tab
 * bar. Trade terms (CK, FMH, 3-way match) stay identical in both languages —
 * translating them would make the app harder to talk about on the floor, not
 * easier. copy-shared.test.ts pins EN against nav.ts/TabBar so this file
 * cannot silently drift from the labels it is meant to replace.
 *
 * `NAV_COPY` covers every SupplyNavKey value — compile-enforced via
 * `Record<SupplyNavKey, Copy>`, so adding a nav key without adding its copy
 * fails tsc, not a runtime probe.
 *
 * Two keys render in two different chrome pieces with two different EN
 * forms: the four SupplyTabBar keys (order/receive/count/waste) use the
 * bar's short pill label ("Count", not nav.ts's "Stock count"); the other keys
 * render only in the More sheet, where nav.ts's full label already fits.
 * Each SupplyNavKey only ever appears in ONE of the two surfaces (the bar's
 * four keys are excluded from the sheet — see SupplyMoreSheet's BAR_KEYS),
 * so one Copy pair per key is unambiguous.
 */
import type { Copy, SupplyLang } from "./lang";
import type { SupplyNavKey } from "./nav";

/**
 * Why the Central Kitchen is present but not usable on a given surface.
 *
 * BM/EN like everything else the floor reads — the first cut of this shipped
 * three English string literals onto a BM-default surface, and worse, replaced
 * transfer's existing localized ckOnlyEmptyNote with one of them.
 */
export const CK_UNAVAILABLE_COPY = {
  /** Home: only a scoped (non-see-all) session is actually refused here. */
  home: {
    ms: "Dapur Pusat tiada dashboard sendiri — buka dari Terima, Kira, Buangan, Susut atau Stok ada.",
    en: "Production has no dashboard of its own — open it from Receive, Count, Waste, Shrink or Stock on hand.",
  },
  transfer: {
    ms: "Dapur Pusat tak guna transfer — stok keluar ikut order outlet kat CK board.",
    en: "Production does not transfer — its outbound stock moves through outlet orders on the CK board.",
  },
  /** Order, when supply_vendor_pos_enabled is off. */
  ckNoVendorPos: {
    ms: "Order Dapur Pusat perlu PO vendor dihidupkan.",
    en: "Ordering at Production needs vendor POs switched on.",
  },
} satisfies Record<string, Copy>;

export const NAV_COPY: Record<SupplyNavKey, Copy> = {
  order: { ms: "Order", en: "Order" },
  checks: { ms: "Semakan Terimaan", en: "Receiving checks" },
  // The approver's cross-outlet list — "Semua order" (all orders), distinct
  // from the floor's concise "Order" door above.
  orders: { ms: "Semua order", en: "Orders" },
  alibaba: { ms: "Pesanan Alibaba", en: "Alibaba orders" },
  deliveries: { ms: "Penghantaran Lalamove", en: "Lalamove deliveries" },
  receive: { ms: "Terima", en: "Receive" },
  count: { ms: "Kira", en: "Count" },
  transfer: { ms: "Pindah stok", en: "Transfer" },
  // Concise action verb; "Buang / rosak" read as disconnected fragments.
  waste: { ms: "Buang", en: "Waste" },
  ck: { ms: "Pengeluaran CK", en: "CK production" },
  stor: { ms: "Stor China", en: "China store" },
  "ck-transfer-prices": { ms: "Harga pindahan CK", en: "CK transfer prices" },
  // "Stok semasa" (current stock), not a literal "stock in hand" calque.
  "on-hand": { ms: "Stok semasa", en: "Stock on hand" },
  "opening-stock": { ms: "Stok pembukaan", en: "Opening stock" },
  "food-cost": { ms: "Kos makanan", en: "Food cost" },
  "waste-counts": { ms: "Buangan & kiraan", en: "Waste & counts" },
  shrink: { ms: "Susut & hilang", en: "Waste & shrink" },
  // "Belanja", not the government-report register "Perbelanjaan".
  spend: { ms: "Belanja & harga naik", en: "Spend & price creep" },
  // Trade term — identical in both languages (this file's own rule above).
  match: { ms: "3-way match", en: "3-way match" },
  interco: { ms: "Antara syarikat", en: "Inter-company" },
  // Close module (Finance + HQ month close).
  close: { ms: "Tutup bulan", en: "Month close" },
  "close-transfers": { ms: "Pindahan · semua outlet", en: "Transfers · all outlets" },
  "close-stock": { ms: "Stok penutup", en: "Closing stock" },
  credits: { ms: "Kredit pembekal", en: "Supplier credits" },
  approvals: { ms: "Kelulusan", en: "Approvals" },
  hygiene: { ms: "Senarai semak data", en: "Hygiene queue" },
  history: { ms: "Sejarah FMH", en: "FMH history" },
  // BM noun order is head-first: "item induk" (item [that is] master), not
  // the English-calqued "induk item".
  items: { ms: "Item induk", en: "Item master" },
  // Manage-tier desk surfaces (English-first per the app-copy rule); EN must
  // match nav.ts's label verbatim (copy-shared coverage test).
  canvas: { ms: "Kanvas pautan", en: "Link canvas" },
  "menu-coverage": { ms: "Liputan menu", en: "Menu coverage" },
  packaging: { ms: "Bungkusan", en: "Packaging" },
  plumbing: { ms: "Kesihatan integrasi", en: "Integration health" },
  "xero-map": { ms: "Pemetaan Xero", en: "Xero mapping" },
};

/** Chrome strings that are not tied to a single nav key. */
export const COMMON: {
  back: Copy;
  save: Copy;
  cancel: Copy;
  tryAgain: Copy;
  notSure: Copy;
  /** The tab bar's fifth slot / the sheet's own trigger button. */
  more: Copy;
  /** The More sheet's way back to the /supply dashboard. */
  home: Copy;
  /** Assistive chrome (aria) — PR4 copy pass, audit #11: these reached BM
   *  users in English. "Supply" itself is the product name in both. */
  supplyHomeAria: Copy;
  howToGuideAria: Copy;
  supplyNavAria: Copy;
  moreSectionsAria: Copy;
  allSectionsAria: Copy;
  /** Visible eyebrow at the top of the More sheet (the aria above names the dialog). */
  allSectionsTitle: Copy;
  closeAria: Copy;
} = {
  back: { ms: "Kembali", en: "Back" },
  save: { ms: "Simpan", en: "Save" },
  cancel: { ms: "Batal", en: "Cancel" },
  tryAgain: { ms: "Cuba lagi", en: "Try again" },
  notSure: { ms: "Tak pasti", en: "Not sure" },
  more: { ms: "Lagi", en: "More" },
  home: { ms: "Utama", en: "Home" },
  supplyHomeAria: { ms: "Laman utama Supply", en: "Supply home" },
  howToGuideAria: { ms: "Cara guna Supply — panduan pengguna", en: "How to use Supply — the user guide" },
  supplyNavAria: { ms: "Navigasi Supply", en: "Supply navigation" },
  moreSectionsAria: { ms: "Bahagian lain", en: "More sections" },
  allSectionsAria: { ms: "Semua bahagian Supply", en: "All Supply sections" },
  allSectionsTitle: { ms: "Semua bahagian", en: "All sections" },
  closeAria: { ms: "Tutup", en: "Close" },
};

/** Data-layer fallbacks for a name that failed to resolve (PR4, audit #11:
 *  "Unknown item" reached BM floor users). Loaders without a `lang` in scope
 *  use the ms half directly — the floor is BM-first and a missing-name row is
 *  a data defect, not a register choice. */
export const UNKNOWN_ITEM: Copy = { ms: "Barang tak dikenali", en: "Unknown item" };
export const UNKNOWN_PRODUCT: Copy = { ms: "Produk tak dikenali", en: "Unknown product" };

/** The one place a caller resolves a nav label out of NAV_COPY. */
export function navLabel(key: SupplyNavKey, lang: SupplyLang): string {
  return NAV_COPY[key][lang];
}

/**
 * The header's user menu + Desk/Simple view toggle — both previously
 * hardcoded English literals (2026-08-17 header redesign). "Desk" and
 * "Simple" stay identical in both languages, same rule as NAV_COPY's trade
 * terms: they are this app's own names for the two floor-view layouts, not
 * ordinary nouns to translate.
 *
 * Day/Night register note (revised after review round 1 — was "cerah"):
 * NOT "Siang"/"Malam" (time-of-day, reads like a clock) and NOT the standing
 * assumption that "Terang"/"Gelap" is too literal a calque — it turns out to
 * be the opposite: "terang/gelap" is what Malaysian phone display settings
 * actually use ("Mod Terang"/"Mod Gelap" on a stock Malay-locale phone), so
 * it IS the floor's natural register, not literary. "Cerah" carries a
 * weather/mood sense instead (hari cerah = a sunny day) that reads slightly
 * off for a screen setting. "Skrin gelap" was already right; only the Day
 * side changed, to "Skrin terang".
 */
export const HEADER_COPY: {
  accountMenu: Copy;
  appearance: Copy;
  dayTheme: Copy;
  nightTheme: Copy;
  language: Copy;
  deskView: Copy;
  simpleView: Copy;
  outletMenu: Copy;
  outletGroupAll: Copy;
  signOut: Copy;
  signingOut: Copy;
} = {
  accountMenu: { ms: "Menu akaun", en: "Account menu" },
  appearance: { ms: "Skrin — terang atau gelap", en: "Appearance — day or night" },
  dayTheme: { ms: "Skrin terang", en: "Day" },
  nightTheme: { ms: "Skrin gelap", en: "Night" },
  // The user menu's Language section heading (round 2 of the 2026-08-17
  // header redesign — BM|EN moved off the header bar and in here, beside
  // Appearance). The two OPTIONS inside stay literal "Bahasa Malaysia" /
  // "English" — a language's own name, unlike a heading, doesn't change
  // when you change the UI language.
  language: { ms: "Bahasa", en: "Language" },
  // The header pill's dropdown (2026-08-19 flow rework — the pill switches
  // outlets in place; the full-page picker is first-landing only now).
  outletMenu: { ms: "Tukar outlet", en: "Switch outlet" },
  outletGroupAll: { ms: "HQ · semua outlet", en: "HQ · all outlets" },
  deskView: { ms: "Paparan Desk (jadual penuh)", en: "Desk view (full grid)" },
  simpleView: { ms: "Paparan Simple (langkah demi langkah)", en: "Simple view (step by step)" },
  // Sign out (2026-08-20). Supply shipped without any sign-out control at all
  // while hq, chef and ops each had one — an oversight, not a decision. "Log
  // keluar" is the floor's own register for this; "Keluar" alone is already
  // taken by the count screens for taking an item OUT of service, so reusing
  // it here would collide on the one screen where both could appear.
  signOut: { ms: "Log keluar", en: "Sign out" },
  signingOut: { ms: "Sedang log keluar…", en: "Signing out…" },
};
